/* jshint esversion: 6 */

// Use the named `Intercom` export rather than the default. The SDK is compiled
// TypeScript that tags itself `__esModule: true`, which makes webpack 5 skip its
// default-interop wrapper — a default-import binding then points at the module
// namespace object and calling it throws TypeError at runtime.
import { Intercom as IntercomInit } from '@intercom/messenger-js-sdk';

const APP_ID = 'v3ka5gj1';

// Fetch a replacement this many seconds before the token expires. Mirrors
// Dlvr\IntercomIdentity::REFRESH_SKEW; the server sends the matching lifetime as
// `intercomJwtTtl` so the two only have to agree on the margin, not the lifetime.
const REFRESH_SKEW_SECONDS = 300;

// Floor on the refresh delay, so an unexpectedly short TTL degrades into slow
// polling rather than a request loop.
const MIN_REFRESH_SECONDS = 60;

// Tracks the current Intercom session state so transitions between anonymous and
// identified visitors trigger a shutdown+boot (the SDK can't promote a session via
// `update` alone).
let bootedAs = 'none';

// Identity-token refresh state. `requestToken` is supplied by the app shell —
// this module deliberately knows nothing about how the token is fetched.
let requestToken = null;
let refreshTimer = null;
let tokenAppliedAt = 0;
let tokenTtlSeconds = 0;
let watchingVisibility = false;

// Whether the app has asked for the launcher to be hidden. Kept here, and
// replayed through buildSettings, because `boot` resets every setting: the app
// hides the launcher on mount (mobile), then user detail arrives, bootIntercom
// sees the identity change and does shutdown + boot — and the launcher comes
// back. Holding the intent in the module makes it survive that.
let launcherHidden = false;

function isIdentified(userDetail) {
    return Boolean(userDetail && userDetail.id && userDetail.email);
}

function buildSettings(userDetail) {
    const settings = { app_id: APP_ID, hide_default_launcher: launcherHidden };

    if (!isIdentified(userDetail)) {
        return settings;
    }

    if (userDetail.intercomJwt) {
        settings.intercom_user_jwt = userDetail.intercomJwt;
    }

    return settings;
}

function clearRefreshTimer() {
    if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer);
        refreshTimer = null;
    }
}

function refreshDelaySeconds() {
    return Math.max(tokenTtlSeconds - REFRESH_SKEW_SECONDS, MIN_REFRESH_SECONDS);
}

function scheduleRefresh() {
    clearRefreshTimer();

    if (!requestToken || !tokenTtlSeconds) {
        return;
    }

    refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        requestToken();
    }, refreshDelaySeconds() * 1000);
}

// setTimeout does not run while the machine is suspended and is throttled hard in
// background tabs, so a tab reopened after a long idle can be holding an expired
// token with its refresh still pending. Re-check age on every return to visibility.
function handleVisibilityChange() {
    if (document.visibilityState !== 'visible' || bootedAs !== 'identified' || !requestToken) {
        return;
    }

    const ageSeconds = (Date.now() - tokenAppliedAt) / 1000;
    if (tokenTtlSeconds && ageSeconds >= refreshDelaySeconds()) {
        clearRefreshTimer();
        requestToken();
    }
}

// Record the lifetime of the token just handed to Intercom and arm the timer that
// replaces it. Called on every identified boot/update, so a token arriving from
// any source (account fetch, profile update, scheduled refresh) resets the clock.
function noteAppliedToken(userDetail) {
    tokenAppliedAt = Date.now();
    tokenTtlSeconds = Number(userDetail.intercomJwtTtl) || 0;
    scheduleRefresh();
}

export function bootIntercom(userDetail) {
    const target = isIdentified(userDetail) ? 'identified' : 'anonymous';
    const settings = buildSettings(userDetail);

    if (bootedAs === 'none') {
        IntercomInit(settings);
        bootedAs = target;
        if (settings.intercom_user_jwt) {
            noteAppliedToken(userDetail);
        }
        return;
    }

    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
        return;
    }

    if (bootedAs !== target) {
        window.Intercom('shutdown');
        window.Intercom('boot', settings);
        bootedAs = target;
    } else {
        window.Intercom('update', settings);
    }

    if (settings.intercom_user_jwt) {
        noteAppliedToken(userDetail);
    } else {
        clearRefreshTimer();
        tokenTtlSeconds = 0;
    }
}

// Register the callback this module uses to obtain a replacement identity token.
// Without it the Messenger keeps its first token until the page reloads, and any
// session outliving that token drops back to an unverified visitor — which costs
// Intercom its trusted account link.
export function startIdentityRefresh(onTokenNeeded) {
    requestToken = onTokenNeeded;

    if (!watchingVisibility && typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        watchingVisibility = true;
    }

    // A token may already be in play if the identified boot beat this call.
    scheduleRefresh();
}

export function setLauncherVisible(visible) {
    // Recorded before the guard, so an ask that lands before the Messenger is
    // ready still reaches it through the next boot rather than being dropped.
    launcherHidden = !visible;

    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
        return;
    }
    window.Intercom('update', { hide_default_launcher: launcherHidden });
}

export function openMessenger() {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
        return;
    }
    window.Intercom('show');
}

// Call before a logout-triggered page navigation. Tells Intercom to clear
// its cookies/localStorage so the next visitor on this browser starts as a
// fresh anonymous user rather than inheriting the previous user's session
// (which would expose their conversation history to whoever logs in next).
export function shutdownIntercom() {
    if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
        window.Intercom('shutdown');
    }
    bootedAs = 'none';
    clearRefreshTimer();
    tokenTtlSeconds = 0;
}
